// Paso 3 — las líneas.
//
// Dos decisiones que se ven poco y pesan mucho:
//
//   · la celda que lleva un valor PROPIO se acentúa. Sin esa marca no hay forma
//     de saber de un vistazo qué se ha tocado, y con setenta líneas eso es
//     perderlo;
//   · «aplicar a todas» no copia la cabecera a cada línea: les QUITA el valor
//     propio. El efecto visible es el mismo, pero además las deja siguiendo los
//     cambios de cabecera que vengan después (ver `restablecerTodas`).
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { AlmacenVenta, CabeceraBorrador, LineaBorrador, LineaRecuperable } from '@/types'
import {
  almacenEfectivo,
  cuantasTocadas,
  esRepetida,
  fechaEfectiva,
  fueRedondeada,
  referenciasRepetidas,
  tieneValorPropio,
} from '@/utils/pedidoDesdeArchivo'
import { chipCls, cromado, etiquetaCls } from './cromado'

interface Props {
  dark: boolean
  cab: CabeceraBorrador
  lineas: LineaBorrador[]
  recuperables: LineaRecuperable[]
  almacenes: AlmacenVenta[]
  onLinea: (id: string, patch: Partial<LineaBorrador>) => void
  onQuitar: (id: string) => void
  onRestablecer: (campo?: 'almacen' | 'fecha') => void
  onRecuperar: (r: LineaRecuperable, indice: number) => void
}

export function PasoLineas({
  dark, cab, lineas, recuperables, almacenes,
  onLinea, onQuitar, onRestablecer, onRecuperar,
}: Props) {
  const { t } = useTranslation()
  const c = cromado(dark)
  const [abiertas, setAbiertas] = useState(false)
  const repes = referenciasRepetidas(lineas)
  const tocadas = cuantasTocadas(lineas)

  const celdaBase = `px-1.5 py-1 rounded-md border text-[11px] w-full transition-colors
    focus:outline-none focus:ring-2 focus:ring-sky-500/30 focus:border-sky-500`
  const propioAcento = dark
    ? 'border-sky-500/40 bg-sky-500/[0.08] text-sky-200'
    : 'border-sky-400 bg-sky-50 text-sky-900'
  const heredado = dark
    ? 'border-white/10 bg-white/[0.03] text-slate-400'
    : 'border-slate-200 bg-white text-slate-600'

  return (
    <div className="space-y-4">
      {/* ── Acciones sobre todas ─────────────────────────────────────────── */}
      <div className={`flex items-center gap-2 flex-wrap px-3 py-2 rounded-lg border ${c.panel} ${c.panelBorde}`}>
        <span className={`${etiquetaCls} ${c.mutedTxt} mr-1`}>
          {t('salesOrders.desdeArchivo.aplicar')}
        </span>
        <button
          type="button"
          onClick={() => onRestablecer('fecha')}
          className={`px-2.5 py-1 rounded-md text-[11px] cursor-pointer transition-colors ${
            dark ? 'bg-white/[0.06] text-slate-300 hover:bg-white/[0.1]'
                 : 'bg-white text-slate-600 hover:bg-slate-100 border border-slate-200'
          }`}
        >
          {t('salesOrders.desdeArchivo.aplicarFecha')}
        </button>
        <button
          type="button"
          onClick={() => onRestablecer('almacen')}
          className={`px-2.5 py-1 rounded-md text-[11px] cursor-pointer transition-colors ${
            dark ? 'bg-white/[0.06] text-slate-300 hover:bg-white/[0.1]'
                 : 'bg-white text-slate-600 hover:bg-slate-100 border border-slate-200'
          }`}
        >
          {t('salesOrders.desdeArchivo.aplicarAlmacen')}
        </button>
        <span className={`text-[11px] ml-auto ${tocadas > 0 ? (dark ? 'text-sky-400' : 'text-sky-600') : c.dimTxt}`}>
          {tocadas > 0
            ? t('salesOrders.desdeArchivo.tocadas', { count: tocadas })
            : t('salesOrders.desdeArchivo.ningunaTocada')}
        </span>
      </div>

      {/* ── Tabla ────────────────────────────────────────────────────────── */}
      <div className={`rounded-xl border overflow-hidden ${c.panelBorde}`}>
        <table className="w-full text-left">
          <thead>
            <tr className={`${dark ? 'bg-white/[0.03]' : 'bg-slate-50'}`}>
              <th className={`px-2 py-2 ${etiquetaCls} ${c.dimTxt} w-8`}>#</th>
              <th className={`px-2 py-2 ${etiquetaCls} ${c.dimTxt} w-40`}>
                {t('salesOrders.desdeArchivo.referencia')}
              </th>
              {/* La descripción la trae el flujo de lectura (`Descripcion` /
                  `Description`). Si el archivo no la traía, la celda lleva un
                  guion: dejarla en blanco se leería como «no hay artículo». */}
              <th className={`px-2 py-2 ${etiquetaCls} ${c.dimTxt}`}>
                {t('salesOrders.desdeArchivo.descripcion')}
              </th>
              <th className={`px-2 py-2 ${etiquetaCls} ${c.dimTxt} w-24 text-right`}>
                {t('salesOrders.desdeArchivo.unidades')}
              </th>
              <th className={`px-2 py-2 ${etiquetaCls} ${c.dimTxt} w-24`}>
                {t('salesOrders.desdeArchivo.almacen')}
              </th>
              <th className={`px-2 py-2 ${etiquetaCls} ${c.dimTxt} w-36`}>
                {t('salesOrders.desdeArchivo.fecha')}
              </th>
              <th className="w-8" />
            </tr>
          </thead>
          <tbody>
            {lineas.map((l, i) => {
              const repetida = esRepetida(l, repes)
              const redondeada = fueRedondeada(l)
              return (
                <tr
                  key={l.id}
                  className={`border-t ${c.panelBorde} ${
                    tieneValorPropio(l) ? (dark ? 'bg-sky-500/[0.03]' : 'bg-sky-50/40') : ''
                  }`}
                >
                  <td className={`px-2 py-1.5 text-[10px] tabular-nums ${c.dimTxt}`}>{i + 1}</td>

                  <td className="px-2 py-1.5 min-w-0 align-top">
                    <div className={`text-[12px] font-medium font-mono truncate ${c.mainTxt}`}>
                      {l.referencia}
                    </div>
                    <div className="flex items-center gap-1.5 flex-wrap mt-0.5">
                      {l.ean ? (
                        <span className={`text-[10px] font-mono ${c.dimTxt}`}>{l.ean}</span>
                      ) : (
                        <span className={chipCls(dark, 'aviso')}>
                          {t('salesOrders.desdeArchivo.chips.sinEan')}
                        </span>
                      )}
                      {repetida && (
                        <span className={chipCls(dark, 'info')}>
                          {t('salesOrders.desdeArchivo.chips.repetida')}
                        </span>
                      )}
                      {l.recuperada && (
                        <span className={chipCls(dark, 'nuevo')}>
                          {t('salesOrders.desdeArchivo.chips.recuperada')}
                        </span>
                      )}
                    </div>
                  </td>

                  <td className="px-2 py-1.5 min-w-0 align-top">
                    {l.descripcion ? (
                      <span
                        className={`block text-[11px] leading-snug truncate ${c.mutedTxt}`}
                        title={l.descripcion}
                      >
                        {l.descripcion}
                      </span>
                    ) : (
                      <span className={`text-[11px] ${c.dimTxt}`}>—</span>
                    )}
                  </td>

                  <td className="px-2 py-1.5">
                    <input
                      type="number"
                      min={0}
                      value={l.unidades}
                      onChange={(e) => onLinea(l.id, { unidades: Number(e.target.value) })}
                      className={`${celdaBase} ${heredado} text-right tabular-nums`}
                    />
                    {redondeada && (
                      // El usuario tiene que poder ver que se le ha cambiado la
                      // cantidad que pedía el cliente.
                      <div className={`text-[9px] text-right mt-0.5 ${c.dimTxt}`}>
                        {t('salesOrders.desdeArchivo.pedia', { n: l.unidadesSinRedondear })}
                      </div>
                    )}
                  </td>

                  <td className="px-2 py-1.5">
                    <select
                      value={l.almacen}
                      onChange={(e) => onLinea(l.id, { almacen: e.target.value })}
                      title={l.almacen ? undefined : t('salesOrders.desdeArchivo.heredaCabecera')}
                      className={`${celdaBase} cursor-pointer ${l.almacen ? propioAcento : heredado}`}
                    >
                      <option value="">{almacenEfectivo(l, cab) || '—'}</option>
                      {almacenes.map((a) => (
                        <option key={a.codigo} value={a.codigo}>{a.codigo}</option>
                      ))}
                    </select>
                  </td>

                  <td className="px-2 py-1.5">
                    <input
                      type="date"
                      value={fechaEfectiva(l, cab)}
                      onChange={(e) => onLinea(l.id, {
                        // Volver a poner la fecha de cabecera es volver a heredar:
                        // así el usuario puede deshacer sin un botón extra.
                        fecha: e.target.value === cab.fecha ? '' : e.target.value,
                      })}
                      className={`${celdaBase} ${l.fecha ? propioAcento : heredado}`}
                    />
                  </td>

                  <td className="px-1 py-1.5">
                    <button
                      type="button"
                      onClick={() => onQuitar(l.id)}
                      aria-label={t('salesOrders.desdeArchivo.quitarLinea')}
                      className={`p-1 rounded cursor-pointer transition-colors ${
                        dark ? 'text-slate-600 hover:text-red-400 hover:bg-white/6'
                             : 'text-slate-300 hover:text-red-500 hover:bg-slate-100'
                      }`}
                    >
                      <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                        <path d="M18 6L6 18M6 6l12 12" />
                      </svg>
                    </button>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>

        {lineas.length === 0 && (
          <p className={`px-4 py-8 text-center text-[12px] ${c.dimTxt}`}>
            {t('salesOrders.desdeArchivo.sinLineas')}
          </p>
        )}
      </div>

      {/* ── Lo que este cliente quitó antes ──────────────────────────────── */}
      {recuperables.length > 0 && (
        <div className={`rounded-xl border overflow-hidden ${c.panelBorde}`}>
          <button
            type="button"
            onClick={() => setAbiertas((v) => !v)}
            className={`w-full flex items-center gap-2 px-3.5 py-2.5 cursor-pointer transition-colors ${
              dark ? 'hover:bg-white/[0.03]' : 'hover:bg-slate-50'
            }`}
          >
            <svg
              className={`w-3 h-3 transition-transform ${abiertas ? 'rotate-90' : ''} ${c.dimTxt}`}
              viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"
            >
              <path d="M9 18l6-6-6-6" />
            </svg>
            <span className={`text-[11px] ${c.mutedTxt}`}>
              {t('salesOrders.desdeArchivo.recuperables', { count: recuperables.length })}
            </span>
          </button>

          {abiertas && (
            <div className={`border-t ${c.panelBorde}`}>
              {recuperables.map((r, i) => (
                <div
                  key={`${r.referencia}-${r.color}-${r.estilo}-${i}`}
                  className={`flex items-center gap-3 px-3.5 py-2 ${i > 0 ? `border-t ${c.panelBorde}` : ''}`}
                >
                  <span className={`text-[12px] font-mono flex-1 min-w-0 truncate ${c.mainTxt}`}>
                    {r.referencia}
                    {(r.color || r.estilo) && (
                      <span className={`ml-2 text-[10px] ${c.dimTxt}`}>
                        {[r.color, r.estilo].filter(Boolean).join(' · ')}
                      </span>
                    )}
                  </span>
                  <span className={`text-[11px] tabular-nums ${c.mutedTxt}`}>{r.cantidad}</span>
                  <button
                    type="button"
                    onClick={() => onRecuperar(r, i)}
                    className="px-2.5 py-1 rounded-md text-[11px] font-medium bg-sky-600 text-white hover:bg-sky-500 cursor-pointer transition-colors"
                  >
                    {t('salesOrders.desdeArchivo.anadir')}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
